'use client'

import { useMemo, useState } from 'react'

import type { AnalysisLawnContext } from '@/hooks/use-homepage-analysis'
import type { UserLawn } from '@/hooks/use-default-lawn'

export type AnalysisLawnChoice = string | null | undefined

export interface AnalysisLawnContextState {
  choiceRequired: boolean
  choiceMade: boolean
  choice: AnalysisLawnChoice
  selectedLawn: UserLawn | null
  lawnContext: AnalysisLawnContext | undefined
  selectLawn: (lawnId: string | null) => void
}

/**
 * Keeps scan attribution simple without guessing among saved lawns:
 *
 * - zero lawns: unassigned
 * - one lawn: automatic
 * - multiple lawns: one contextual choice, including "just scan"
 * - a valid route lawn: explicit and locked by the caller
 */
export function useAnalysisLawnContext(
  lawns: UserLawn[],
  requestedLawnId?: string | null,
): AnalysisLawnContextState {
  // undefined = not answered yet; null = explicitly continue unassigned.
  const [manualChoice, setManualChoice] = useState<AnalysisLawnChoice>(undefined)

  const requestedLawn = requestedLawnId
    ? lawns.find((lawn) => lawn.id === requestedLawnId) ?? null
    : null
  const choiceRequired = lawns.length > 1 && !requestedLawn
  const validManualChoice = typeof manualChoice === 'string' &&
    lawns.some((lawn) => lawn.id === manualChoice)
      ? manualChoice
      : manualChoice === null
        ? null
        : undefined
  const choice: AnalysisLawnChoice = requestedLawn
    ? requestedLawn.id
    : lawns.length === 1
      ? lawns[0].id
      : choiceRequired
        ? validManualChoice
        : null
  const choiceMade = !choiceRequired || choice !== undefined
  const selectedLawn = typeof choice === 'string'
    ? lawns.find((lawn) => lawn.id === choice) ?? null
    : null

  const lawnContext = useMemo<AnalysisLawnContext | undefined>(() => {
    if (!selectedLawn) return undefined
    return {
      lawnId: selectedLawn.id,
      knownGrassType: selectedLawn.grass_type ?? undefined,
      grassTypeProvenance: 'saved_lawn',
      lawnContextIntent: requestedLawn
        ? 'route_locked'
        : lawns.length === 1
          ? 'automatic_single'
          : 'user_selected',
    }
  }, [lawns.length, requestedLawn, selectedLawn])

  return {
    choiceRequired,
    choiceMade,
    choice,
    selectedLawn,
    lawnContext,
    selectLawn: setManualChoice,
  }
}
